import type { RootStore } from 'logic';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { STEP_TAB_DONE, STEP_TAB_TRIAGE } from './ui-active-session';

export enum DownloadFolderNaming {
  CARD_TITLE = 'cardtitle',
  FILENAME = 'filename',
}

export type CardSource = 'selected' | 'step' | 'allCards';

async function createFolder(rootDirEntry: FileSystemDirectoryHandle, folders: string[]) {
  const promises: Array<Promise<unknown>> = [];
  folders.forEach((folderName) => {
    if (folderName === '.' || folderName === '') {
      return;
    }
    promises.push(
      rootDirEntry.getDirectoryHandle(folderName, { create: true }).catch((e) => {
        console.error(`Failed to create folder for ${folderName}:`, e);
      }),
    );
  });

  return Promise.allSettled(promises);
}

export class UiDownloadFoldersStore {
  private rootStore: RootStore;
  folder: FileSystemDirectoryHandle | undefined;
  folderNaming: DownloadFolderNaming | null = null;
  cardSource: CardSource = 'step';
  selectedStepId: string = STEP_TAB_TRIAGE;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      cardSource: observable,
      selectedStepId: observable,
      folder: observable,
      folderNaming: observable,
      isDialogOpen: computed,
      selectedFolderName: computed,
      selectFolder: action,
      cardIdsToUse: computed,
      foldersToCreate: computed,
      openDialog: action.bound,
      setCardSource: action.bound,
      setSelectedStepId: action.bound,
      setFolderNaming: action.bound,
      closeDialog: action.bound,
    });
  }

  get isDialogOpen() {
    return !!this.folderNaming;
  }

  // eslint-disable-next-line class-methods-use-this
  get canUseDownloadFolders() {
    return typeof window.showDirectoryPicker === 'function';
  }

  get selectedFolderName() {
    return this.folder ? this.folder.name : undefined;
  }

  /** Resolve card IDs for a given step ID */
  private getCardIdsForStep(stepId: string): string[] {
    const { currentSession } = this.rootStore.sessionsStore;
    if (!currentSession) return [];

    if (stepId === STEP_TAB_DONE) {
      return currentSession.doneCardIdsByStep;
    }
    if (stepId === STEP_TAB_TRIAGE) {
      return currentSession.getCardIdsByStepId(null);
    }
    return currentSession.getCardIdsByStepId(stepId);
  }

  get cardIdsToUse(): string[] {
    const { currentSession } = this.rootStore.sessionsStore;
    if (!currentSession) return [];

    switch (this.cardSource) {
      case 'selected':
        return this.rootStore.uiActiveSessionStore.selectedCardIdsInCurrentStep;
      case 'step':
        return this.getCardIdsForStep(this.selectedStepId);
      case 'allCards':
        return currentSession.inProgressVisibleCardIds;
      default:
        return [];
    }
  }

  get foldersToCreate() {
    const { cardsStore } = this.rootStore;
    const cards = this.cardIdsToUse.map((id) => cardsStore.cards[id]).filter(Boolean);

    const folderNames = (
      this.folderNaming === DownloadFolderNaming.CARD_TITLE
        ? cards.filter((c) => c.labelInfo.errors.length === 0).map((card) => card.mainLabel)
        : cards.filter((c) => !c.filenameNeedsAttention).map((card) => card.filename)
    ).filter((name) => !!name && name.length > 0);
    return folderNames;
  }

  openDialog = (naming: DownloadFolderNaming = DownloadFolderNaming.FILENAME) => {
    this.folderNaming = naming;
    const { uiActiveSessionStore } = this.rootStore;
    if (uiActiveSessionStore.hasSelectedCards) {
      this.cardSource = 'selected';
    } else {
      this.cardSource = 'step';
    }
    // Default step to whatever tab the user is currently viewing
    this.selectedStepId = uiActiveSessionStore.currentStepTabId;
  };

  closeDialog = () => {
    this.folderNaming = null;
  };

  setCardSource = (source: CardSource) => {
    this.cardSource = source;
  };

  setSelectedStepId = (stepId: string) => {
    this.selectedStepId = stepId;
  };

  setFolderNaming = (naming: DownloadFolderNaming) => {
    this.folderNaming = naming;
  };

  selectFolder = async () => {
    try {
      const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
      runInAction(() => {
        this.folder = handle;
      });
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        // User cancelled the picker — do nothing
        return;
      }
      console.error('Failed to select folder:', e);
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `Could not access folder: ${e?.message || 'Permission denied'}`,
      });
    }
  };

  createFolders = async () => {
    if (!this.folder) {
      alert('A folder must be selected');
      return;
    }

    const results = await createFolder(this.folder, this.foldersToCreate);
    const successFolders = results.filter((r) => r.status === 'fulfilled');
    if (this.foldersToCreate.length > successFolders.length) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `Successfully created ${successFolders.length} folders. Failed to create ${
          this.foldersToCreate.length - successFolders.length
        } folders.`,
      });
    } else if (this.foldersToCreate.length > 0) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `Successfully created ${successFolders.length} folders`,
      });
    } else {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'There were no folders to create',
      });
    }
  };
}
